import { useCallback } from 'react';
import type { Observable } from 'rxjs';
import { useSecuritiesContext } from '../contexts';
import { CUSTOMER_MARKET_DATA_SNAPSHOT } from '../tokens';
import type { CustomerMarketDataSnapshotRequest } from '../types';
import { EMPTY_ARRAY } from '../utils';
import { useMultiMarketDataSnapshotBase } from './useMultiMarketDataSnapshotBase';
import { useSyncedRef } from './useSyncedRef';

export type UseMultiCustomerMarketDataSnapshotRequestParams = Pick<CustomerMarketDataSnapshotRequest, 'SizeBuckets'>;

export interface UseMultiCustomerMarketDataSnapshotRequest {
  /** Logical cache/subscription key, usually produced by `getCustomerMarketDataSnapshotRequestKey`. */
  key: string;
  Symbol: string;
  Counterparty: string;
  SizeBuckets?: UseMultiCustomerMarketDataSnapshotRequestParams['SizeBuckets'];
}

/**
 * Builds the logical request key used by `useMultiCustomerMarketDataSnapshot`.
 *
 * This mirrors the standard MDS key contract, but customer MDS identity is symbol + counterparty +
 * optional size buckets. The shared base hook uses this key to dedupe subscriptions, cache the latest
 * snapshot, and expose `requestKey` on emitted data.
 */
export function getCustomerMarketDataSnapshotRequestKey({
  Symbol: symbol,
  Counterparty: counterparty,
  SizeBuckets: sizeBuckets,
}: Pick<UseMultiCustomerMarketDataSnapshotRequest, 'Symbol' | 'Counterparty' | 'SizeBuckets'>) {
  return [symbol, counterparty, ...(sizeBuckets?.length ? [`sizeBuckets:${sizeBuckets.join(',')}`] : EMPTY_ARRAY)]
    .filter(Boolean)
    .join('|');
}

interface UseMultiCustomerMarketDataParams {
  /** One request entry per customer MDS stream. This should be a ReplaySubject. */
  requestsObs: Observable<UseMultiCustomerMarketDataSnapshotRequest[]>;
  /** The maximum amount of subscriptions we dare keep open with the backend. */
  maxActiveSubscriptions: number;
  /** The tag to put on the subscription for better debugging of all related streams to the hook invocation. */
  subscriptionTag?: string;
}

function getCustomerMarketDataRequestKey(request: UseMultiCustomerMarketDataSnapshotRequest) {
  return request.key;
}

/**
 * Subscribes to customer-specific MDS streams for many logical requests.
 *
 * This hook only handles customer MDS request shaping. Subscription lifecycle, stale request cleanup,
 * cache storage, and emitted `requestKey` annotation are shared with standard MDS through
 * `useMultiMarketDataSnapshotBase`.
 */
export const useMultiCustomerMarketDataSnapshot = ({
  requestsObs,
  maxActiveSubscriptions,
  subscriptionTag,
}: UseMultiCustomerMarketDataParams) => {
  const { securitiesBySymbol } = useSecuritiesContext();
  const securitiesBySymbolRef = useSyncedRef(securitiesBySymbol);

  const buildSubscriptionRequest = useCallback(
    ({
      Symbol: symbol,
      Counterparty: counterparty,
      SizeBuckets: sizeBuckets,
    }: UseMultiCustomerMarketDataSnapshotRequest): CustomerMarketDataSnapshotRequest | undefined => {
      if (!symbol || !counterparty) {
        return undefined;
      }

      return {
        name: CUSTOMER_MARKET_DATA_SNAPSHOT,
        Symbol: symbol,
        Counterparty: counterparty,
        PriceIncrement: securitiesBySymbolRef.current.get(symbol)?.DefaultPriceIncrement,
        tag: subscriptionTag ?? 'MultiCustomerMarketDataSnapshot',
        ...(sizeBuckets ? { SizeBuckets: sizeBuckets } : {}),
      };
    },
    [securitiesBySymbolRef, subscriptionTag]
  );

  return useMultiMarketDataSnapshotBase({
    requestsObs,
    maxActiveSubscriptions,
    getRequestKey: getCustomerMarketDataRequestKey,
    buildSubscriptionRequest,
  });
};
